import { z } from "zod";

// HAR-style header entry used inside both request and response objects
const harHeaderSchema = z.object({
  name: z.string(),
  value: z.string(),
});

// Schema for network logs stored at networkLogsPath.
// Each item is a HAR-like entry wrapped in a { json: ... } envelope.
export const appetizeNetworkLogSchema = z.object({
  type: z.enum(["request", "response", "error"]),
  requestId: z.string().optional(),
  request: z.object({
    method: z.string(),
    url: z.string(),
    httpVersion: z.string().optional(),
    authority: z.string().optional(),
    cookies: z.array(z.unknown()).optional(),
    headers: z.array(harHeaderSchema).optional(),
    queryString: z.array(z.unknown()).optional(),
    headersSize: z.number().optional(),
    bodySize: z.number().optional(),
    postData: z
      .object({
        mimeType: z.string().optional(),
        text: z.string().optional(),
      })
      .optional(),
  }),
  response: z
    .object({
      status: z.number(),
      statusText: z.string().optional(),
      httpVersion: z.string().optional(),
      cookies: z.array(z.unknown()).optional(),
      headers: z.array(harHeaderSchema).optional(),
      redirectURL: z.string().optional(),
      headersSize: z.number().optional(),
      bodySize: z.number().optional(),
      content: z
        .object({
          size: z.number().optional(),
          mimeType: z.string().optional(),
          compression: z.number().optional(),
          text: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  cache: z.record(z.unknown()).optional(),
  timings: z
    .object({
      send: z.number().optional(),
      wait: z.number().optional(),
      ssl: z.number().optional(),
      connect: z.number().optional(),
      receive: z.number().optional(),
    })
    .optional(),
  startedDateTime: z.string().optional(),
  time: z.number().optional(),
  error: z
    .object({
      errorText: z.string(),
    })
    .optional(),
  relativeTimestamp: z.number(),
  absoluteTimestamp: z.number(),
});

export type AppetizeNetworkLog = z.infer<typeof appetizeNetworkLogSchema>;

export const appetizeNetworkLogsArraySchema = z.array(appetizeNetworkLogSchema);

// ─────────────────────────────────────────────────────────────────────────────

// Schema for network logs stored in the sessionId zip (Appetize Clone format).
// Flat structure with top-level method / status_code / host / path fields.
export const appetizeCloneNetworkLogSchema = z.object({
  host: z.string().nullish(),
  path: z.string().nullish(),
  scheme: z.string().nullish(),
  method: z.string().nullish(),
  status_code: z.number().nullish(),
  error: z.string().nullish(),
  request_headers: z.record(z.string(), z.string()).nullish(),
  response_headers: z.record(z.string(), z.string()).nullish(),
  request_body: z.unknown().optional(),
  response_body: z.unknown().optional(),
  // Seconds since epoch (use DateTime.fromSeconds)
  timestamp_start: z.number().nullish(),
  timestamp_end: z.number().nullish(),
});

export type AppetizeCloneNetworkLog = z.infer<
  typeof appetizeCloneNetworkLogSchema
>;

export const appetizeCloneNetworkLogsArraySchema = z.array(
  appetizeCloneNetworkLogSchema
);
